import { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type { LargestClassifiedBackflowResponse } from '@metron/shared';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toErrorMessage } from '@metron/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export function LargestClassifiedBackflowPage() {
  const apiClient = useApiClient();
  const [data, setData] = useState<LargestClassifiedBackflowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hideExtra, setHideExtra] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const url = hideExtra
          ? 'backflow/largest-classified?hideExtra=true'
          : 'backflow/largest-classified';
        const response = await apiClient.get<LargestClassifiedBackflowResponse>(url);
        setData(response);
      } catch (err) {
        setError(
          toErrorMessage(err, 'Failed to fetch classified backflow data'),
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [apiClient, hideExtra]);

  const chartData = data?.backflowByDay.map((item) => ({
    day: new Date(item.day).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    }),
    extraCount: item.extraCount,
    otherCount: item.otherCount,
  })) ?? [];

  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Largest Classified Backflow</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Largest Classified Backflow</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div className='space-y-2'>
        <h1 className='text-3xl font-bold'>Largest Classified Backflow</h1>
        <p className='text-sm text-muted-foreground'>
          Meters with the most backflow events by ML classification. "Failure Analysis" and T2
          internal customers are excluded.
        </p>
        <p className='text-sm text-muted-foreground'>
          "Extra" meters are those whose consumer name contains the substring "Extra"
          (case-insensitive).
        </p>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Classified Backflow Meter Count</CardTitle>
          <CardDescription>
            Number of meters with classified backflow events (last 7 days)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0
            ? (
              <ResponsiveContainer width='100%' height={350}>
                <BarChart data={chartData}>
                  <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                  <XAxis dataKey='day' tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0].payload;
                      const total = (d.extraCount ?? 0) + (d.otherCount ?? 0);
                      return (
                        <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
                          <p className='font-semibold'>{d.day}</p>
                          <p className='text-sm'>
                            <span className='font-medium'>{total}</span> total meters
                          </p>
                          <p className='text-sm text-red-600'>
                            <span className='font-medium'>{d.extraCount ?? 0}</span> Extra meters
                          </p>
                          <p className='text-sm text-red-300'>
                            <span className='font-medium'>{d.otherCount ?? 0}</span> Other meters
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Bar dataKey='extraCount' stackId='backflow' fill='#ef4444' name='Extra Meters' />
                  <Bar dataKey='otherCount' stackId='backflow' fill='#fca5a5' name='Other Meters' />
                </BarChart>
              </ResponsiveContainer>
            )
            : (
              <div className='flex items-center justify-center h-[350px]'>
                <p className='text-muted-foreground'>
                  No classified backflow detected in the last 7 days
                </p>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Meters Table */}
      <Card>
        <CardHeader>
          <CardTitle>Meters with Largest Classified Backflow</CardTitle>
          <CardDescription>
            Top 100 meters ordered by total backflow rate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2 mb-4'>
            <Checkbox
              id='hide-extra-classified'
              checked={hideExtra}
              onCheckedChange={(checked) => setHideExtra(checked === true)}
            />
            <label htmlFor='hide-extra-classified' className='text-sm cursor-pointer'>
              Hide Extra meters
            </label>
          </div>
          {data?.meters && data.meters.length > 0
            ? (
              <div className='max-h-[600px] overflow-y-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Meter ID</TableHead>
                      <TableHead>Consumer Name</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead className='text-right'>Total Backflow Rate</TableHead>
                      <TableHead className='text-right'>Events</TableHead>
                      <TableHead className='text-right'>Avg Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.meters.map((meter, i) => (
                      <TableRow key={meter.sensorId}>
                        <TableCell>
                          <a
                            href={`https://plus.waterscope.us/portfolio/${meter.customerId}/${meter.allSensorsGroupId}/${meter.allSensorsGroupId}/${meter.sensorId}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:underline'
                            // Rows without a meter ID would otherwise all share the link text
                            // "Unknown meter"; give each a distinct accessible name by position.
                            aria-label={meter.meterId
                              ? undefined
                              : `Unknown meter, row ${i + 1} — view in WaterScope Plus`}
                          >
                            {meter.meterId || 'Unknown meter'}
                          </a>
                        </TableCell>
                        <TableCell>{meter.consumerName || '\u2014'}</TableCell>
                        <TableCell>{meter.meterSize || '-'}</TableCell>
                        <TableCell className='text-right font-medium text-red-600'>
                          {meter.totalBackflowRate.toLocaleString(undefined, {
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell className='text-right'>{meter.backflowEventCount}</TableCell>
                        <TableCell className='text-right'>
                          {meter.avgBackflowRate.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
            : (
              <div className='flex items-center justify-center h-[350px]'>
                <p className='text-muted-foreground'>
                  No meters with classified backflow found
                </p>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
